import { getCasesApi, getMainPageApi } from "../../../api/api";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useScrollContext } from "../../../lib/scroll/scroll-context";
import { TCase } from "../../../types/types";
import Case from "../../common/cases-mock/case";
import Loading from "../../common/loading/loading";
import BlockTitle from "../block-title/block-title";
import { CASES_LIMIT, LAYOUT_DESKTOP_MIN_WIDTH_PX } from "../../../const/const";
import "./cases-block.sass";

type TitlePhase = "sticky" | "fading" | "released";

export const CasesBlock = () => {
  const [cases, setCases] = useState<TCase[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [requestKey, setRequestKey] = useState(0);
  const [titlePhase, setTitlePhase] = useState<TitlePhase>("sticky");
  const titlePhaseRef = useRef<TitlePhase>("sticky");
  const stackRef = useRef<HTMLDivElement>(null);
  const { bindScrollHandler, unbindScrollHandler } = useScrollContext();

  const isAbortError = (error: unknown) =>
    error instanceof DOMException && error.name === "AbortError";

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);

    Promise.all([
      getMainPageApi({ signal: controller.signal }),
      getCasesApi({ signal: controller.signal }).catch(() => ({
        data: [] as TCase[],
      })),
    ])
      .then(([mainData, casesData]) => {
        const nextCases = (
          Array.isArray(mainData?.data?.cases) ? mainData.data.cases : []
        ) as TCase[];
        const allCases: TCase[] = Array.isArray(casesData?.data)
          ? casesData.data
          : [];
        const bySlug = new Map(allCases.map((c) => [c.slug, c] as const));
        const merged = nextCases.map((c: TCase) => {
          const full = bySlug.get(c.slug);
          if (!full) {
            return c;
          }
          return {
            ...c,
            tagsList: full.tagsList ?? c.tagsList ?? [],
            case_tags: full.case_tags ?? c.case_tags ?? [],
          };
        });
        setCases(merged);
        setLoadError("");
      })
      .catch((error) => {
        if (isAbortError(error)) {
          return;
        }
        setLoadError("Не удалось загрузить кейсы главной страницы.");
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      });
    return () => {
      controller.abort();
    };
  }, [requestKey]);

  const visibleCases = Array.isArray(cases)
    ? (() => {
        const sortedCases = [...cases].sort((a, b) => {
          const extractFirstDigit = (slug: string): number => {
            const match = slug.match(/^(\d)/);
            return match ? parseInt(match[1]) : 0;
          };

          return extractFirstDigit(a.slug) - extractFirstDigit(b.slug);
        });

        return sortedCases.length > CASES_LIMIT
          ? sortedCases.slice(0, CASES_LIMIT)
          : sortedCases;
      })()
    : [];

  useLayoutEffect(() => {
    if (isLoading) {
      return;
    }
    let cancelled = false;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!cancelled) {
          ScrollTrigger.refresh();
        }
      });
    });
    return () => {
      cancelled = true;
    };
  }, [isLoading]);

  useLayoutEffect(() => {
    if (isLoading || !Array.isArray(cases) || cases.length === 0) {
      return;
    }
    const stack = stackRef.current;
    if (!stack) {
      return;
    }

    const desktopMq = window.matchMedia(
      `(min-width: ${LAYOUT_DESKTOP_MIN_WIDTH_PX}px)`,
    );

    const clearUnified = () => {
      stack.classList.remove("cases-block__stack--measured");
      stack.style.removeProperty("--cases-unified-min-height");
    };

    const applyUnified = () => {
      if (!desktopMq.matches) {
        clearUnified();
        ScrollTrigger.refresh();
        return;
      }
      const items = stack.querySelectorAll<HTMLElement>(".case-item");
      if (items.length === 0) {
        clearUnified();
        ScrollTrigger.refresh();
        return;
      }
      let maxH = 0;
      items.forEach((el) => {
        maxH = Math.max(maxH, Math.ceil(el.getBoundingClientRect().height));
      });
      if (maxH <= 0) {
        clearUnified();
        ScrollTrigger.refresh();
        return;
      }
      const next = `${maxH}px`;
      const prev = stack.style.getPropertyValue("--cases-unified-min-height");
      stack.classList.add("cases-block__stack--measured");
      stack.style.setProperty("--cases-unified-min-height", next);
      if (prev !== next) {
        ScrollTrigger.refresh();
      }
    };

    const ro = new ResizeObserver(() => {
      requestAnimationFrame(applyUnified);
    });
    const items = stack.querySelectorAll<HTMLElement>(".case-item");
    items.forEach((el) => ro.observe(el));

    applyUnified();
    desktopMq.addEventListener("change", applyUnified);

    return () => {
      desktopMq.removeEventListener("change", applyUnified);
      ro.disconnect();
      clearUnified();
      ScrollTrigger.refresh();
    };
  }, [isLoading, cases]);

  useEffect(() => {
    titlePhaseRef.current = titlePhase;
  }, [titlePhase]);

  const syncTitleStick = useCallback(() => {
    const stack = stackRef.current;
    if (!stack) {
      return;
    }
    const phase = titlePhaseRef.current;
    const last = stack.querySelector<HTMLElement>(
      ".case.caseItemAnchor:last-of-type",
    );
    if (!last) {
      if (phase !== "sticky") {
        titlePhaseRef.current = "sticky";
        setTitlePhase("sticky");
      }
      return;
    }
    const stickyTopPx = parseFloat(getComputedStyle(last).top);
    if (!Number.isFinite(stickyTopPx)) {
      return;
    }
    const r = last.getBoundingClientRect();
    const enter = r.top <= stickyTopPx + 3;
    const exitScrollUp = r.top > stickyTopPx + 72;
    let next: TitlePhase = phase;
    if (exitScrollUp) {
      next = "sticky";
    } else if (enter && phase === "sticky") {
      next = "fading";
    }
    if (next !== phase) {
      titlePhaseRef.current = next;
      setTitlePhase(next);
    }
  }, []);

  const onTitleBarTransitionEnd = (
    e: React.TransitionEvent<HTMLDivElement>,
  ) => {
    if (e.propertyName !== "opacity" || e.target !== e.currentTarget) {
      return;
    }
    setTitlePhase((p) => (p === "fading" ? "released" : p));
  };

  useEffect(() => {
    if (isLoading || visibleCases.length === 0) {
      titlePhaseRef.current = "sticky";
      setTitlePhase("sticky");
      return;
    }
    const handler = () => {
      syncTitleStick();
    };
    bindScrollHandler(handler);
    const onResize = () => {
      syncTitleStick();
    };
    window.addEventListener("resize", onResize, { passive: true });
    requestAnimationFrame(() => {
      syncTitleStick();
    });
    return () => {
      unbindScrollHandler(handler);
      window.removeEventListener("resize", onResize);
    };
  }, [
    isLoading,
    visibleCases.length,
    bindScrollHandler,
    unbindScrollHandler,
    syncTitleStick,
  ]);

  useEffect(() => {
    if (titlePhase !== "fading") {
      return;
    }
    const id = window.setTimeout(() => {
      setTitlePhase((p) => (p === "fading" ? "released" : p));
    }, 620);
    return () => window.clearTimeout(id);
  }, [titlePhase]);

  return (
    <div className="cases-block">
      <div className="cases-block__surface">
        <div
          className={`cases-block__deco${titlePhase === "fading" ? " cases-block__deco--fadeOut" : ""}${titlePhase === "released" ? " cases-block__deco--released" : ""}`}
          aria-hidden
        >
          <div className="cases-block__bg cases-block__bg-left" />
          <div className="cases-block__bg cases-block__bg-right" />
        </div>

        <div className="cases-block__main">
          <div
            className={`cases-block__titleBar${titlePhase === "fading" ? " cases-block__titleBar--fadeOut" : ""}${titlePhase === "released" ? " cases-block__titleBar--stackEnd" : ""}`}
            onTransitionEnd={onTitleBarTransitionEnd}
          >
            <BlockTitle>наши кейсы</BlockTitle>
          </div>

          <div className="cases-block__stack" ref={stackRef}>
            {visibleCases.map((caseData) => (
              <Case
                key={caseData.id}
                caseData={caseData}
                trimDescriptionToContent={true}
              />
            ))}
          </div>
        </div>

        <div className="cases-block__block">
          {loadError && <div className="error">{loadError}</div>}
          {loadError && (
            <button
              type="button"
              className="link"
              onClick={() => setRequestKey((prev) => prev + 1)}
            >
              Повторить загрузку
            </button>
          )}

          {!isLoading && !loadError && visibleCases.length === 0 && (
            <div className="error">Кейсы не найдены.</div>
          )}

          <Loading isLoading={isLoading} />
        </div>
      </div>
    </div>
  );
};
